import { useState, useRef } from 'react';
import type { ServiceDefinition } from '@om/shared-types';
import { Plus, Search, X } from 'lucide-react';
import { serviceSuggestionApiService } from '@/lib/api/serviceSuggestion.service';

interface ServiceDiscoverySearchProps {
  category: string;
  onAdd: (service: ServiceDefinition) => void;
}

export function ServiceDiscoverySearch({ category, onAdd }: ServiceDiscoverySearchProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<ServiceDefinition[]>([]);
  const [loading, setLoading] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  async function handleSearch(value: string) {
    setQuery(value);
    if (value.trim().length < 2) {
      setResults([]);
      return;
    }
    setLoading(true);
    try {
      const res = await serviceSuggestionApiService.searchDefinitions(category, value.trim());
      setResults(res.definitions ?? []);
    } catch {
      // silently clear results on error
      setResults([]);
    } finally {
      setLoading(false);
    }
  }

  function handleOpen() {
    setOpen(true);
    setTimeout(() => inputRef.current?.focus(), 50);
  }

  function handleClose() {
    setOpen(false);
    setQuery('');
    setResults([]);
  }

  function handleAdd(svc: ServiceDefinition) {
    onAdd(svc);
    handleClose();
  }

  if (!open) {
    return (
      <button
        type="button"
        onClick={handleOpen}
        className="mt-1 flex items-center gap-1.5 rounded-md border border-dashed border-gray-300 px-3 py-1.5 text-xs text-gray-500 hover:border-primary hover:text-primary transition-colors"
      >
        <Plus className="h-3.5 w-3.5" />
        Add a service
      </button>
    );
  }

  return (
    <div className="mt-2 rounded-md border border-gray-200 bg-white p-2 shadow-sm">
      <div className="flex items-center gap-2">
        <Search className="h-4 w-4 flex-shrink-0 text-gray-400" />
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={(e) => handleSearch(e.target.value)}
          placeholder={`Search ${category} services…`}
          className="flex-1 text-sm outline-none placeholder-gray-400"
        />
        <button type="button" onClick={handleClose} className="text-gray-400 hover:text-gray-600">
          <X className="h-4 w-4" />
        </button>
      </div>
      {loading && (
        <p className="mt-2 text-xs text-gray-400">Searching…</p>
      )}
      {!loading && results.length > 0 && (
        <ul className="mt-2 divide-y divide-gray-100">
          {results.map((svc) => (
            <li key={svc.id}>
              <button
                type="button"
                onClick={() => handleAdd(svc)}
                className="w-full rounded px-2 py-1.5 text-left text-sm hover:bg-gray-50"
              >
                <span className="font-medium text-gray-800">{svc.name}</span>
                {svc.description && (
                  <span className="ml-1 text-xs text-gray-500">— {svc.description}</span>
                )}
              </button>
            </li>
          ))}
        </ul>
      )}
      {!loading && query.length >= 2 && results.length === 0 && (
        <p className="mt-2 text-xs text-gray-400">No results found.</p>
      )}
    </div>
  );
}
